/**
 * revisedAudioRefs — the client-side twin of `api/_utils/audioAccess.ts`'s
 * `fetchRevisedAudioRefs` / `stampRowAudioRefs`.
 *
 * WHY THIS EXISTS. Per-clip versioned audio refs (`<uuid>.v<N>`) are stamped
 * onto content rows by the SERVER routes — `/api/courses/:code/cycles` and
 * `/infplay-cycles`. But the player does not get all of its content from those
 * routes: `generateLearningScript` walks `course_legos` /
 * `course_practice_phrases` / `course_seeds` / `listening_pod_sentences` /
 * `course_audio` DIRECTLY over Supabase, and its output is what the script
 * cache stores and what the returning-learner cache fast path
 * (`LearningPlayer.vue`, "CACHE FAST PATH") hydrates SimplePlayer from —
 * pre-empting the /cycles bootstrap entirely.
 *
 * An unstamped (bare-uuid) ref for a REVISED clip is a permanent stale-audio
 * bug, because both downstream caches key on the ref string:
 *   - IndexedDB `ssi-audio-cache-v2` keys by audio id, so the pre-repair blob
 *     is returned for the bare uuid forever;
 *   - the browser HTTP cache keys by URL, and `/api/audio/:id` sets
 *     `Cache-Control: public, max-age=31536000, immutable`.
 * Dropping the script cache on an `audio_stamp` move does NOT fix this: the
 * regenerated walk produces bare uuids again. The suffix has to be applied at
 * the walk, which is what this module does.
 *
 * Same shape as the server helper deliberately: look up only the ids on the
 * rows about to be stamped, keep the ones that have been revised, then rewrite
 * just those ids on the fetched rows. Everything unrevised keeps its
 * bare uuid, its URL and its cached bytes.
 */

import type { SupabaseClient } from '@supabase/supabase-js'

/**
 * The denormalised audio-id columns the script walk reads. Mirrors
 * `AUDIO_ID_COLUMNS` in `api/_utils/audioAccess.ts`; `id` is here as well
 * because the walk also reads `course_audio` rows directly (listen bookends,
 * presentation backfill) where the clip's own primary key IS the ref.
 */
const AUDIO_ID_COLUMNS = [
  'id',
  'known_audio_id',
  'target1_audio_id',
  'target2_audio_id',
  'presentation_audio_id',
  'target_audio_id',
  'intro_audio_id',
  'outro_audio_id',
  'audio_uuid',
] as const

/**
 * Audio-id columns holding an ARRAY of ids. `listening_pod_sentences` splits a
 * silence-split turn into per-sentence clips here, and those ids become the
 * per-sentence `target_audio_id` / `known_audio_id` downstream — so leaving
 * them bare would defeat the stamping of the row they came from.
 */
const AUDIO_ID_ARRAY_COLUMNS = [
  'sentence_audio_ids',
  'sentence_known_audio_ids',
  'takeg_audio_ids',
] as const

/**
 * `course_audio.id` is a uuid column. The walk's rows also carry their OWN
 * primary keys in `id` — `spa_for_eng:S0001L01B01` on a practice phrase,
 * `spa_for_eng:pod-1:SC01-S001` on a pod sentence — and one of those inside an
 * `id=in.(…)` filter makes Postgres reject the whole chunk with a 400 (`invalid
 * input syntax for type uuid`). That was 31-40 failed requests on every
 * spa_for_eng boot, and every failed chunk left 150 genuine clip ids unstamped
 * and asked again by the next lane (job #860). Only a uuid can be a clip.
 */
const CLIP_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export function isClipId(id: string): boolean {
  return CLIP_ID.test(id)
}

/** Build `<uuid>.v<N>`; revision 1 (or absent) stays a bare uuid. */
export function buildAudioRef(id: string, revision: number | null | undefined): string {
  return revision && revision > 1 ? `${id}.v${revision}` : id
}

/**
 * How many ids go into one `.in('id', …)` filter. PostgREST carries the list in
 * the URL; 150 uuids is ~6 KB, inside every proxy limit. Mirrors
 * `REVISED_REFS_ID_CHUNK` in `api/_utils/audioAccess.ts`.
 */
const REVISED_REFS_ID_CHUNK = 150

/**
 * Above this many unresolved ids, a caller that DECLARES a whole-course walk
 * (generateLearningScript) gets one course-wide query instead of hundreds of
 * id chunks. It is opt-in, never inferred from the count: on the client a big
 * id set is usually just a big window — the boot's 30-seed basket load is
 * 3,213 ids on spa_for_eng, and inferring "whole course" from that pulled all
 * 39,699 revised rows (837 KB) on every boot (job #812, measured on dev).
 */
const REVISED_REFS_WHOLE_COURSE_THRESHOLD = REVISED_REFS_ID_CHUNK * 20

/**
 * Every audio id `stampRowAudioRefs` would look at on these rows, as bare
 * uuids. Reads the SAME column lists stamping reads, which is what makes the
 * narrowed lookup safe: no id that gets stamped can be missing from the query.
 */
export function collectAudioIds(...rowSets: ReadonlyArray<ReadonlyArray<unknown>>): Set<string> {
  const ids = new Set<string>()
  for (const rows of rowSets) {
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue
      const src = row as Record<string, unknown>
      for (const col of AUDIO_ID_COLUMNS) {
        const v = src[col]
        if (typeof v === 'string' && v) ids.add(bareAudioId(v))
      }
      for (const col of AUDIO_ID_ARRAY_COLUMNS) {
        const v = src[col]
        if (!Array.isArray(v)) continue
        for (const id of v) if (typeof id === 'string' && id) ids.add(bareAudioId(id))
      }
    }
  }
  return ids
}

type RevisionRow = { id: string; audio_revision: number | null }

/**
 * Look up which of `ids` are revised. Null on ANY failure, so the caller can
 * tell "none of these are revised" from "we could not ask" — only the first may
 * be remembered. `wholeCourse` asks for every revised clip in the course in one
 * query instead, for walks that touch most of it anyway.
 */
async function lookupRevisions(
  supabase: SupabaseClient,
  courseCode: string,
  ids: string[],
  wholeCourse: boolean,
): Promise<RevisionRow[] | null> {
  try {
    if (wholeCourse) {
      const { data, error } = await supabase
        .from('course_audio')
        .select('id, audio_revision')
        .eq('course_code', courseCode)
        .gt('audio_revision', 1)
      return error || !data ? null : (data as RevisionRow[])
    }
    const chunks: string[][] = []
    for (let i = 0; i < ids.length; i += REVISED_REFS_ID_CHUNK) {
      chunks.push(ids.slice(i, i + REVISED_REFS_ID_CHUNK))
    }
    const results = await Promise.all(
      chunks.map((chunk) =>
        supabase
          .from('course_audio')
          .select('id, audio_revision')
          // NO course filter on an id lookup (job #338). A clip's revision is a
          // property of the CLIP, not of the course that points at it, and pods
          // are per LANGUAGE: a course's pod lines often point at clips another
          // course recorded (78 such pod clips measured 2026-10-24). Filtering
          // to this course left exactly those bare, so a re-recorded borrowed
          // clip kept playing its cached old bytes forever. The id is the
          // primary key; the filter never narrowed anything else.
          .in('id', chunk)
          .gt('audio_revision', 1),
      ),
    )
    const rows: RevisionRow[] = []
    for (const { data, error } of results) {
      if (error || !data) return null
      rows.push(...(data as RevisionRow[]))
    }
    return rows
  } catch {
    return null
  }
}

/**
 * Strip a `.vN` suffix back to the bare uuid.
 *
 * Needed wherever a stamped ref has to go back to the DATABASE — `course_audio`
 * is keyed by the bare uuid, so an `.in('id', …)` filter built from stamped
 * refs matches nothing. Mirrors `parseAudioRef` on the server.
 */
export function bareAudioId(ref: string): string {
  return ref.replace(/\.v\d+$/, '')
}

/** Apply a revised-ref map to one audio id, leaving unrevised ids untouched. */
export function applyAudioRef(
  refs: Map<string, string>,
  audioId: string | null | undefined,
): string | null {
  if (!audioId) return audioId ?? null
  return refs.get(audioId) ?? audioId
}

/**
 * Stamp revised refs onto the audio-id columns of fetched content rows.
 *
 * Returns fresh objects; the input rows are not mutated. A no-op (and no copy)
 * when the course has no revised clips, which is the overwhelming common case.
 */
export function stampRowAudioRefs<T>(refs: Map<string, string>, rows: T[]): T[] {
  if (refs.size === 0) return rows
  return rows.map((row) => {
    if (!row || typeof row !== 'object') return row
    const src = row as Record<string, unknown>
    let next: Record<string, unknown> | null = null
    for (const col of AUDIO_ID_COLUMNS) {
      const current = src[col]
      if (typeof current !== 'string') continue
      const stamped = refs.get(current)
      if (!stamped) continue
      if (!next) next = { ...src }
      next[col] = stamped
    }
    for (const col of AUDIO_ID_ARRAY_COLUMNS) {
      const current = src[col]
      if (!Array.isArray(current)) continue
      if (!current.some((id) => typeof id === 'string' && refs.has(id))) continue
      if (!next) next = { ...src }
      next[col] = current.map((id) => (typeof id === 'string' ? refs.get(id) ?? id : id))
    }
    return (next ?? row) as T
  })
}

// ── Shared per-course map, filled just in time ──────────────────────────────
//
// Several lanes walk Supabase for audio ids independently — the script walk,
// the pod lap scheduler, the Layer-1 scheduler, the listening overlay, the
// pronunciation overlay, CourseDataProvider. They share one map per course.
//
// JUST IN TIME (Tom, 2026-09-23: "fetch no more than [what's needed] at first,
// then top up ahead of the learner"). This used to download EVERY revised clip
// in the course on first use — 39,699 rows for spa_for_eng, 353 KB for
// zho_for_eng, on every boot (job #804). Now each lane passes the ids off the
// rows it is about to stamp, and only ids this session has not already
// resolved go to the database. The map grows with what the learner is about to
// hear, exactly like the server routes' window (api/_utils/audioAccess.ts).
//
// A FAILED lookup is never remembered: its ids stay unresolved and the next
// lane to need them asks again. Remembering the failure would strand a session
// on bare refs after one transient error. An id that came back unrevised IS
// remembered — that is an answer, not a failure.
interface CourseRevisionState {
  refs: Map<string, string>
  resolved: Set<string>
  inFlight: Map<string, Promise<void>>
  wholeCourse: boolean
}
const revisionStateByCourse = new Map<string, CourseRevisionState>()

/**
 * True when every one of these ids has had its revision looked up successfully
 * for this course. A failed lookup leaves its ids unresolved (they are asked
 * again next time), so a caller that caches stamped rows must check this
 * before treating them as final — else a repaired clip plays under its old id
 * for the rest of the session (job #804, found by the #816 cold-verify).
 */
export function revisionsResolved(courseCode: string, ids: Iterable<string>): boolean {
  const state = revisionStateByCourse.get(courseCode)
  if (state?.wholeCourse) return true
  for (const raw of ids) if (raw && !state?.resolved.has(bareAudioId(raw))) return false
  return true
}

/**
 * Get the revised-ref map covering `ids` (bare or already-stamped refs), shared
 * across lanes. Pass `collectAudioIds(rows)` for the rows you are about to
 * stamp. The returned map may hold more than you asked for; it never holds
 * less, unless the lookup failed.
 *
 * Never throws; a failed lookup leaves those ids unstamped for this call — a
 * missed suffix costs one stale clip, a throw would cost the whole lane.
 */
export async function getRevisedAudioRefs(
  supabase: SupabaseClient | null | undefined,
  courseCode: string,
  ids: Iterable<string>,
  opts: { wholeCourseWalk?: boolean } = {},
): Promise<Map<string, string>> {
  if (!supabase || !courseCode) return new Map()
  let state = revisionStateByCourse.get(courseCode)
  if (!state) {
    state = { refs: new Map(), resolved: new Set(), inFlight: new Map(), wholeCourse: false }
    revisionStateByCourse.set(courseCode, state)
  }
  const waits = new Set<Promise<void>>()
  const freshSet = new Set<string>()
  for (const raw of ids) {
    if (!raw) continue
    const id = bareAudioId(raw)
    // After a whole-course walk every revised clip OF THIS COURSE is in refs;
    // an id still unseen may be another course's clip (a borrowed pod line),
    // so it is asked by id rather than assumed unrevised (job #338).
    if (state.resolved.has(id)) continue
    if (state.refs.has(id)) { state.resolved.add(id); continue }
    // Not a clip id, so never revised: an answer, not a lookup (job #860).
    if (!isClipId(id)) { state.resolved.add(id); continue }
    const pending = state.inFlight.get(id)
    if (pending) waits.add(pending)
    else freshSet.add(id)
  }
  const fresh = [...freshSet]
  if (fresh.length > 0) {
    const s = state
    const wholeCourse = !!opts.wholeCourseWalk && !state.wholeCourse && fresh.length > REVISED_REFS_WHOLE_COURSE_THRESHOLD
    const lookup = lookupRevisions(supabase, courseCode, fresh, wholeCourse).then((rows) => {
      for (const id of fresh) s.inFlight.delete(id)
      if (!rows) return
      for (const row of rows) if (row?.id) s.refs.set(row.id, buildAudioRef(row.id, row.audio_revision))
      if (wholeCourse) s.wholeCourse = true
      for (const id of fresh) s.resolved.add(id)
    })
    for (const id of fresh) state.inFlight.set(id, lookup)
    waits.add(lookup)
  }
  await Promise.all(waits)
  return state.refs
}

/** Drop the shared map — after a content swap, or for test isolation. */
export function clearRevisedAudioRefs(courseCode?: string): void {
  if (courseCode) revisionStateByCourse.delete(courseCode)
  else revisionStateByCourse.clear()
}
